import { createFileRoute, useNavigate, useSearch } from '@tanstack/react-router'
import { useState, useEffect } from 'react'
import { Gift, CheckCircle, XCircle, Loader2, Clock } from 'lucide-react'
import { AppShell } from '@/shared/ui/app-shell'
import { BottomNav } from '@/widgets/bottom-nav'
import { Button } from '@/shared/ui/button'
import { useGiftCardStatus } from '@/features/gift-cards/api/use-gift-card-status'
import { cn } from '@/shared/lib/utils'

export const Route = createFileRoute('/gift-card-success')({
  component: GiftCardSuccessPage,
  validateSearch: (search: Record<string, unknown>) => ({
    giftCardId: (search.giftCardId as string) || (search.orderId as string) || '',
  }),
})

function GiftCardSuccessPage() {
  const navigate = useNavigate()
  const { giftCardId } = useSearch({ from: '/gift-card-success' })
  const { data, isLoading } = useGiftCardStatus(giftCardId || null)

  const [timedOut, setTimedOut] = useState(false)

  // Таймаут polling — 60 секунд
  useEffect(() => {
    const timer = setTimeout(() => setTimedOut(true), 60000)
    return () => clearTimeout(timer)
  }, [])

  const isPending = (isLoading || !data || data.status === 'pending') && !timedOut
  const isSuccess = data?.status === 'active'
  const isCancelled = data?.status === 'cancelled'
  const isTimedOut = (isLoading || !data || data.status === 'pending') && timedOut

  return (
    <AppShell showBottomNav>
      <div className="flex-1 bg-paper dark:bg-dark-bg flex items-center justify-center px-6 pb-24">
        <div className="w-full max-w-sm text-center">

          {/* Pending */}
          {isPending && (
            <>
              <div className="w-16 h-16 rounded-full bg-gray-100 dark:bg-dark-card flex items-center justify-center mx-auto mb-6">
                <Loader2 className="w-8 h-8 text-gray-400 animate-spin" />
              </div>
              <h1 className="text-xl font-semibold text-ink dark:text-dark-text mb-2">Проверяем оплату...</h1>
              <p className="text-sm text-gray-500">Это займёт несколько секунд</p>
            </>
          )}

          {/* Success */}
          {isSuccess && data && (
            <>
              <div className="w-16 h-16 rounded-full bg-teal-50 dark:bg-teal-900/20 flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-8 h-8 text-teal-600" />
              </div>
              <h1 className="text-xl font-semibold text-ink dark:text-dark-text mb-2">Сертификат оформлен!</h1>
              {data.recipientEmail && (
                <p className="text-sm text-gray-500 mb-6">
                  Письмо отправлено на <span className="font-medium text-ink dark:text-dark-text">{data.recipientEmail}</span>
                </p>
              )}
              <div className="bg-[var(--tg-theme-secondary-bg-color,#F5F5F5)] rounded-2xl p-5 mb-6 border border-[var(--tg-theme-hint-color,#999)]/15">
                <p className="text-lg font-bold text-[var(--tg-theme-text-color,#1A1A1A)]">
                  {data.initialAmount.toLocaleString('ru-RU')} ₽
                </p>
                <p className="text-xs text-[var(--tg-theme-hint-color,#999)] mt-1">
                  Код сертификата отправлен получателю на email
                </p>
              </div>
              <div className="space-y-3">
                <Button
                  variant="primary"
                  size="lg"
                  className="w-full"
                  onClick={() => navigate({ to: '/gift-cards' })}
                >
                  <Gift className="w-4 h-4 mr-2" />
                  Ещё один сертификат
                </Button>
                <Button
                  variant="secondary"
                  size="lg"
                  className="w-full"
                  onClick={() => navigate({ to: '/' })}
                >
                  На главную
                </Button>
              </div>
            </>
          )}

          {/* Cancelled */}
          {isCancelled && (
            <>
              <div className="w-16 h-16 rounded-full bg-red-50 dark:bg-red-900/20 flex items-center justify-center mx-auto mb-6">
                <XCircle className="w-8 h-8 text-red-500" />
              </div>
              <h1 className="text-xl font-semibold text-ink dark:text-dark-text mb-2">Оплата отменена</h1>
              <p className="text-sm text-gray-500 mb-6">Сертификат не был оформлен</p>
              <Button
                variant="primary"
                size="lg"
                className="w-full"
                onClick={() => navigate({ to: '/gift-cards' })}
              >
                Попробовать снова
              </Button>
            </>
          )}

          {/* Timed out */}
          {isTimedOut && (
            <>
              <div className="w-16 h-16 rounded-full bg-gray-100 dark:bg-dark-card flex items-center justify-center mx-auto mb-6">
                <Clock className="w-8 h-8 text-gray-400" />
              </div>
              <h1 className="text-xl font-semibold text-ink dark:text-dark-text mb-2">Оплата не завершена</h1>
              <p className="text-sm text-gray-500 mb-6">Если вы оплатили — письмо придёт автоматически. Если нет — попробуйте ещё раз.</p>
              <div className="space-y-3">
                <Button variant="primary" size="lg" className="w-full" onClick={() => navigate({ to: '/gift-cards' })}>
                  Попробовать снова
                </Button>
                <Button variant="secondary" size="lg" className="w-full" onClick={() => navigate({ to: '/' })}>
                  На главную
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
      <BottomNav />
    </AppShell>
  )
}
